import * as d3 from 'd3';
import { toggleClassByWidth } from '../../js/utils/d3-helpers.ts';
import { BaseChart, UIState } from '../../js/classes/BaseChart.ts';

// D3セレクションの型エイリアスを定義
type D3Selection<T extends d3.BaseType> = d3.Selection<T, unknown, null, undefined>;

/**
 * MonthlyStepsChartに特化したUI状態の型
 */
interface MonthlyStepsUIState extends UIState {
	currentDataPoint: MonthlyStepsItem | undefined;
}

/**
 * 月別歩数アニメーション棒グラフ (MonthlyStepsChart)
 * BaseChartを継承し、d3.intervalベースの独自アニメーションを実装。
 */
export class MonthlyStepsChart extends BaseChart<MonthlyStepsChartData, MonthlyStepsItem, MonthlyStepsUIState> {
	// --- 静的プロパティ ---
	static override defaultConfig = {
		animationInterval: 800,
		autoplay: true,
		autoplayDelay: 500,
		barColorStart: "#a8e6cf",
		barColorEnd: "#2c7a7b",
		yAxisMax: 450000, // ★注: この値はデータがない場合のフォールバックとしてのみ使用
		responsiveBreakpoint: 480
	};

	// --- プロパティ定義 ---
	private allMonthsData: MonthlyStepsItem[] = [];

	private xScale: d3.ScaleBand<string> | null = null;
	private yScale: d3.ScaleLinear<number, number> | null = null;
	private colorScale: d3.ScaleLinear<string, string> | null = null;
	private xAxisGroup: D3Selection<SVGGElement> | null = null;
	private timerText: D3Selection<SVGTextElement> | null = null;

	private animationIntervalId: d3.Timer | null = null;
	private currentStep: number = -1;

	constructor(chartElement: HTMLElement, config = {}) {
		super(chartElement, config);
		this.margin = { top: 20, right: 50, bottom: 80, left: 70 };
	}

public override async init(): Promise<void> {
		try {
			const data = await d3.json<MonthlyStepsChartData>(this.config.jsonUrl!) ?? null;
			
			// セレクタは新仕様の Role属性 を使用する
			const loadingMessage = this.chartElement.querySelector<HTMLElement>('[data-chart-role="loading"]');
			if (loadingMessage) {
				loadingMessage.style.display = 'none';
			}

			this.fullDataSet = data;
			this._processData();
			this.setupDimensions();
			this.setupLayers();
			this._initialDrawAxes();
			this.isReady = true;

			this.updateUI();
		} catch (error) {
			this._displayMessage("データファイルの読み込みに失敗しました。", "error");
			console.error("Failed to load or initialize chart:", error);
			throw error;
		}
	}

	protected override _processData(): void {
		if (!this.fullDataSet || !Array.isArray(this.fullDataSet) || this.fullDataSet.length === 0) {
			this._displayMessage("表示するデータがありません。", "warning");
			this.allMonthsData = [];
			return;
		}
		this.allMonthsData = this.fullDataSet;
		this.totalFrames = this.allMonthsData.length;
	}

	protected override setupLayers(): void {
		d3.select(this.chartElement).select("svg").remove();
		const totalWidth = this.width + this.margin.left + this.margin.right;
		const totalHeight = this.height + this.margin.top + this.margin.bottom;
		
		this.svg = d3.select(this.chartElement).append("svg")
			.attr("width", totalWidth).attr("height", totalHeight);
			
		this.g = this.svg.append("g")
			.attr("transform", `translate(${this.margin.left},${this.margin.top})`);
	}
	
	protected override setupDimensions(): void {
		const containerWidth = this.chartElement.clientWidth;
		if (containerWidth <= 0) return;
		this.width = containerWidth - this.margin.left - this.margin.right;
		this.height = this.width * 0.5;
	}

	protected override _initialDrawAxes(): void {
		if (!this.g || this.allMonthsData.length === 0) return;
		
		this.g.selectAll('*').remove();

		// ▼▼▼ ここから修正 ▼▼▼
		// 1. Y軸の最大値をデータから動的に計算
		const yMaxValue = d3.max(this.allMonthsData, d => d.value) || this.config.yAxisMax;
		const yDomainMax = yMaxValue * 1.1; // 上限に10%の余裕を持たせる

		this.xScale = d3.scaleBand<string>().domain(this.allMonthsData.map(d => d.label))
			.range([0, this.width]).padding(0.2);
			
		this.yScale = d3.scaleLinear()
			.domain([0, yDomainMax]) // 固定値から動的な値に変更
			.range([this.height, 0]);
			
		this.colorScale = d3.scaleLinear<string>()
			.domain([0, this.allMonthsData.length - 1])
			.range([this.config.barColorEnd, this.config.barColorStart]);
			
		this.xAxisGroup = this.g.append("g").attr("class", "x-axis")
			.attr("transform", `translate(0,${this.height})`)
			.call(d3.axisBottom(this.xScale));
			
		// 2. Y軸の目盛りフォーマットを「万」単位に変更
		this.g.append("g").attr("class", "y-axis")
			.call(d3.axisLeft(this.yScale).ticks(5).tickFormat(d => {
				if (d === 0) return "0";
				const valueInMan = (d as number) / 10000;
				// 整数なら小数点以下を表示せず、小数なら表示する
				const format = Number.isInteger(valueInMan) ? "d" : ".1f";
				return `${d3.format(format)(valueInMan)}万`;
			}));
		// ▲▲▲ ここまで修正 ▲▲▲
		
		const totalWidth = this.width + this.margin.left + this.margin.right;
		this.timerText = this.svg!.append("text").attr("class", "chart-timer")
			.attr("x", totalWidth - this.margin.right).attr("y", this.margin.top + this.height)
			.attr("dy", "-0.5em").text("");

		this.checkAndApplyLayout();
	}

	protected override drawFrame(frame: number): void {
		if (frame < 0 || frame >= this.allMonthsData.length || !this.isReady || !this.g || !this.xScale || !this.yScale || !this.colorScale) return;

		this.currentStep = frame;
		const dataToDraw = this.allMonthsData.slice(0, frame + 1);

		this.g.selectAll<SVGRectElement, MonthlyStepsItem>(".bar")
			.data(dataToDraw, d => d.label)
			.join(
				enter => enter.append("rect")
					.attr("class", "bar")
					.attr("x", d => this.xScale!(d.label)!)
					.attr("width", this.xScale!.bandwidth())
					.attr("fill", (d, i) => this.colorScale!(i))
					.attr("y", this.yScale!(0))
					.attr("height", 0)
			)
			.transition().duration(this.config.animationInterval / 2)
			.attr("y", d => this.yScale!(d.value))
			.attr("height", d => this.height - this.yScale!(d.value));

		this.g.selectAll<SVGTextElement, MonthlyStepsItem>(".bar-label")
			.data(dataToDraw, d => d.label)
			.join(
				enter => enter.append("text")
					.attr("class", "bar-label")
					.attr("x", d => this.xScale!(d.label)! + this.xScale!.bandwidth() / 2)
					.attr("y", this.yScale!(0))
					.text("0")
			)
			.transition().duration(this.config.animationInterval / 2)
			.attr("y", d => this.yScale!(d.value) - 5)
			.tween("text", function(d) {
				const element = d3.select(this);
				const currentVal = parseFloat(element.text().replace(/,/g, '')) || 0;
				const i = d3.interpolateNumber(currentVal, d.value);
				return function(t) { element.text(d3.format(",.0f")(i(t))); };
			});
		
		this.updateUI();
	}

	protected override updateUI(): void {
		if (typeof this.onUpdateUICallback === 'function') {
			const payload: MonthlyStepsUIState = {
				isPlaying: this.isPlaying,
				currentFrame: this.currentStep, 
				totalFrames: this.totalFrames,
				currentDataPoint: this.allMonthsData[this.currentStep]
			};
			this.onUpdateUICallback(payload);
		}
		if (this.timerText && this.allMonthsData[this.currentStep]) {
			this.timerText.text(this.allMonthsData[this.currentStep].fullLabel);
		}
	}

	public override play(): void {
		if (this.isPlaying || !this.isReady) return;
		this.isPlaying = true;

		if (this.currentStep >= this.allMonthsData.length - 1) {
			this.currentStep = -1;
			this.g?.selectAll(".bar, .bar-label").remove();
		}

		this.animationIntervalId = d3.interval(() => {
			const nextStep = this.currentStep + 1;
			if (nextStep >= this.allMonthsData.length) {
				this.pause();
				return;
			}
			this.drawFrame(nextStep);
		}, this.config.animationInterval);
		
		this.updateUI();
	}

	public override pause(): void {
		if (!this.isPlaying) return;
		this.isPlaying = false;
		if (this.animationIntervalId) this.animationIntervalId.stop();
		this.animationIntervalId = null;
		this.updateUI();
	}

	public override seek(step: number): void {
		if (!this.isReady) return;
		
		this.pause();

		const newStep = Math.max(0, Math.min(step, this.allMonthsData.length - 1));
		
		this.currentStep = -1;
		this.g?.selectAll(".bar, .bar-label").remove();
		this.drawFrame(newStep);
	}
	
	public override redrawOnResize(): void {
		const stepToRestore = this.currentStep;
		this.pause();
		
		this.setupDimensions();
		this.setupLayers();
		this._initialDrawAxes();
		this.isReady = true;
		
		if(stepToRestore >= 0) {
			this.drawFrame(stepToRestore);
		}
	}
	
	public checkAndApplyLayout(): void {
		if (!this.xAxisGroup || !this.chartElement) return;
		toggleClassByWidth(
			this.xAxisGroup,
			this.chartElement.clientWidth,
			this.config.responsiveBreakpoint,
			'is-narrow'
		);
	}
}